import React, { createContext, useContext, useEffect, useState, useMemo, useCallback } from 'react';
import { jwtDecode } from 'jwt-decode';
import { api } from '../services/api';

interface UserRole {
  id: string;
  user_id: string;
  company_id: string;
  role: string;
  is_active: boolean;
  assigned_by?: string;
}

interface UserCompany {
  id: string;
  company_id: string;
  company?: {
    id: string;
    name: string;
    domain: string;
  };
  userRoles?: UserRole[];
  companyDetails?: {
    id: string;
    name: string;
    domain: string;
  };
}

interface UserComponentAccess {
  component_name: string;
  access_level: 'none' | 'view' | 'edit' | 'manage' | 'restricted';
}

export interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  is_system_admin: boolean; // System admin flag for global permissions override
  profilePicture?: string;
  last_login?: string;
  quote?: string;
  birthMonthDay?: string; // Birth month and day in MM-DD format (no year)
  google_id?: string;
  address?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    zipCode?: string;
    country?: string;
  };
  company?: {
    id: string;
    name: string;
    domain: string;
  };
  // Multi-company support (company-specific roles via UserRole table)
  userCompanies?: UserCompany[];
  companies?: UserCompany[]; // Alias for userCompanies
  // Component access control
  userComponentAccess?: UserComponentAccess[];
  preferences?: {
    [key: string]: any;
  };
  /** Per-user homepage. When set, the `/` root route redirects here on
   *  login instead of the global default (`/my-assignments`). Null means
   *  "use the application default." See plan section 1.6. */
  default_landing_route?: string | null;
  /**
   * @deprecated Do not use. The `users.company_id` field is deprecated.
   * Use `userCompanies` for associations and selection instead.
   */
  company_id?: never;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (token: string) => void;
  logout: () => void;
  loading: boolean;
  /**
   * Merge a partial into the in-memory signed-in user, so a screen that just
   * persisted a profile/preference change can reflect it without mutating the
   * context's user object in place or reloading the page (both of which this
   * replaced).
   *
   * IN-MEMORY ONLY — it does not call the API. Persistence stays with the
   * caller, which owns the endpoint (`PUT /auth/profile` vs `PUT /users/:id`)
   * and the request body; call this after a successful save.
   *
   * `preferences` is REPLACED, not deep-merged: the API stores it as one JSON
   * blob and a `PUT` replaces the whole thing, so callers already compute the
   * merged object for the request body — pass that same value here and the
   * in-memory copy matches exactly what was stored.
   */
  updateUser: (partial: Partial<User>) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeAuth = async () => {
      const storedToken = localStorage.getItem('token');

      if (storedToken) {
        try {
          // Set token in API headers
          api.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;

          // Verify token with backend and get full user data including multi-company info
          const response = await api.get('/auth/verify');
          const userData = response.data.user;



          // Ensure aliases for compatibility
          if (userData.userCompanies) {
            userData.companies = userData.userCompanies;
          }

          setUser(userData);
          setToken(storedToken);
        } catch {
          // Token is invalid, remove it
          localStorage.removeItem('token');
          delete api.defaults.headers.common['Authorization'];
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = useCallback(async (newToken: string) => {
    localStorage.setItem('token', newToken);
    setToken(newToken);
    api.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;

    // Decode token to get user info
    const decoded = jwtDecode(newToken) as any;

    // Fetch full user data including profile picture and component access
    try {
      const response = await api.get(`/users/${decoded.userId}`);
      const userData = response.data.data;

      setUser({
        id: userData.id,
        email: userData.email,
        firstName: userData.first_name,
        lastName: userData.last_name,
        is_system_admin: userData.is_system_admin || false,
        last_login: userData.last_login,
        profilePicture: userData.profile_picture,
        quote: userData.quote,
        birthMonthDay: userData.birth_month_day,
        google_id: userData.google_id,
        address: userData.address,
        company: userData.company,
        userCompanies: userData.userCompanies,
        userComponentAccess: userData.userComponentAccess || [],
        preferences: userData.preferences || {}
      });
    } catch (error) {
      console.error('Failed to fetch user data:', error);
      // Fallback to token data
      setUser({
        id: decoded.userId,
        email: decoded.email,
        firstName: decoded.firstName,
        lastName: decoded.lastName,
        is_system_admin: decoded.is_system_admin || false
      });
    }
  }, []);

  const updateUser = useCallback((partial: Partial<User>) => {
    setUser((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    delete api.defaults.headers.common['Authorization'];
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo(() => ({
    user,
    token,
    login,
    logout,
    loading,
    updateUser
  }), [user, token, login, logout, loading, updateUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

